import React, {useEffect, useState} from 'react';
import {Typography, Card, CardContent} from '@mui/material';
import TrafficLightAlarmActions from '../traffic-lights/TrafficLightAlarmActions.jsx';

const AlarmSummary = ({alarm, availableObjects, trafficLights = []}) => {

    const [summaryAnalysis, setSummaryAnalysis] = useState('');
    const [summaryObjectsPresent, setSummaryObjectsPresent] = useState('');
    const [summaryObjectsMissing, setSummaryObjectsMissing] = useState('');
    const [summaryTextReasoning, setSummaryTextReasoning] = useState('');
    const [summaryAudioMessage, setSummaryAudioMessage] = useState('');


    useEffect(() => {
        let analysis = alarm.analysis || [];
        const summaryAnalysis = analysis.length > 0 ?
            analysis.map((a, index) =>
                (<React.Fragment key={index}> and 🔍
                    analysis:<b>{a.analysisData?.name}({a.analysisData?.model})[{a.analysisData?.class_name}]</b> with
                    value <b>{a.detail}</b> {a.limit_cumulate ? '(Limit cumulate)' : ''}</React.Fragment>)) :
            null;

        setSummaryAnalysis(summaryAnalysis);

        let objects = alarm.objects || [];

        const buildObjectLabel = (obj) => {
            const baseName = obj?.object?.tag?.name || obj?.object?.text || '';
            const childNames = (obj?.object?.tags || []).map((tag) => tag?.name).filter(Boolean);
            const objectLabel = [baseName, ...childNames].filter(Boolean).join('-');
            const detectorLabel = obj?.detector?.text ? `->${obj.detector.text}` : '';
            return `${objectLabel}${detectorLabel}`;
        };

        let objectsSummaryPresent = '';
        let objectsSummaryMissing = '';
        if (objects.length > 0) {
            let presentObjects = objects.filter(obj => !obj.missing).map(buildObjectLabel);
            let missingObjects = objects.filter(obj => obj.missing).map(buildObjectLabel);
            if (presentObjects.length > 0) {
                objectsSummaryPresent += `${presentObjects.join(' + ')}`;
            }
            if (missingObjects.length > 0) {
                objectsSummaryMissing += `${missingObjects.join(' + ')}`;
            }
        }
        setSummaryObjectsPresent(objectsSummaryPresent);
        setSummaryObjectsMissing(objectsSummaryMissing);

        // Handle text reasoning summary
        let textReasoningSummary = '';
        if (alarm.text_reasoning && alarm.text_reasoning.trim() !== '') {
            textReasoningSummary = `🧠 Text reasoning: "${alarm.text_reasoning}"`;
        }
        setSummaryTextReasoning(textReasoningSummary);

        let audioMessageSummary = '';
        if (alarm.audio_message && alarm.audio_message.trim() !== '') {
            audioMessageSummary = `📢 Audio message: "${alarm.audio_message}"`;
        }
        setSummaryAudioMessage(audioMessageSummary);

    }, [alarm]);

    const renderConditionSummary = () => {
        const presentSummary = summaryObjectsPresent ? `Objects present: ${summaryObjectsPresent}` : '';
        const missingSummary = summaryObjectsMissing ? `Objects missing: ${summaryObjectsMissing}` : '';
        const thresholdSummary = alarm.threshold ? (<> when display count
            exceeds <b>{alarm.threshold}</b> time{alarm.threshold > 1 ? "s" : ""}</>) : null;

        const zoneSummary = ` in Zone: ${alarm.zone}`;
        // convert from seconds to human readable format
        let duration = '';
        if (alarm.min_duration > 0) {
            const hours = Math.floor(alarm.min_duration / 3600);
            const minutes = Math.floor((alarm.min_duration % 3600) / 60);
            const seconds = Math.floor(alarm.min_duration % 60);
            duration = `${hours > 0 ? `${hours}h ` : ''}${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`;
        }

        const durationSummary = alarm.min_duration > 0 ? ` for Min. time: ${duration} seconds(${alarm.min_duration})` : '';
        const frequencySummary = alarm.alarm_frequency > 0 ? ` at a frequency of ${alarm.alarm_frequency} seconds` : '';

        return (
            <>
                🚗 {presentSummary && <><b>{presentSummary}</b></>}
                {presentSummary && missingSummary && ' and '}
                {missingSummary && <><b>{missingSummary}</b></>}
                {summaryAnalysis && <><br/>{summaryAnalysis}</>}
                {` ${zoneSummary}`}
                {thresholdSummary}
                {durationSummary && ` ${durationSummary}`}
                {frequencySummary && ` ${frequencySummary}`}
            </>
        );
    };

    const renderAlarmStatusIcon = () => {
        switch (alarm.status) {
            case 'active':
                return '🟢';
            case 'inactive':
                return '🔴';
            default:
                return '';
        }
    };
    if (!alarm) {
        return null;
    }
    if ((!alarm.objects || alarm.objects.length === 0) && (!alarm.analysis || alarm.analysis.length === 0)) {
        return null;
    }

    return (
        <Card sx={{mt: 2}} variant="outlined">
            <CardContent>
                <Typography variant="subtitle">📝{renderAlarmStatusIcon()} Summary of Alarm:</Typography>
                <Typography variant="subtitle2" sx={{mt: 1}}>
                   <em> {alarm.rule}</em>
                </Typography>
                <Typography variant="body2" sx={{mt: 1}}>
                    {renderConditionSummary()}
                </Typography>
                <TrafficLightAlarmActions readOnly actions={alarm.traffic_light_actions || []} lights={trafficLights} />
                {summaryTextReasoning && (
                    <Typography variant="body2" sx={{mt: 1, fontStyle: 'italic', color: 'gray'}}>
                        {summaryTextReasoning}
                    </Typography>
                )}
                {summaryAudioMessage && (
                    <Typography variant="body2" sx={{mt: 1}}>
                        {summaryAudioMessage}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default AlarmSummary;
